/**
 * Configurator link rewriter.
 *
 * Rewrites product card links and navigation links to point to the
 * correct child variant URL when a truck is selected. Restores
 * original URLs when the selection is cleared.
 */

(() => {

// Product handles as they appear in URLs on the site
const PARENT_HANDLES = [
  'diamondbackhd',
  'diamondbackse',
  'diamondback-switchback',
  'diamondback270',
  'diamondback180',
];

// Maps product handles (in URLs) to collection handles (in configurator data)
const PRODUCT_TO_COLLECTION = {
  'diamondbackhd': 'diamondback-hd',
  'diamondbackse': 'diamondback-se',
  'diamondback-switchback': 'diamondback-switchback',
  'diamondback270': 'diamondback-270',
  'diamondback180': 'diamondback-180',
};

const LINK_SELECTOR = [
  'a.grid-view-item__link[href*="/products/diamondback"]',
  'a.list-view-item[href*="/products/diamondback"]',
  'a.grid-view-item__title[href*="/products/diamondback"]',
  '.site-nav__link[href*="/products/diamondback"]',
  'a.site-nav__child-link[href*="/products/diamondback"]',
  'a.collection-grid-item__link[href*="/products/diamondback"]',
  'a[href*="/products/diamondback"]',
].join(', ');

/**
 * Determines which parent handle a link points to.
 * @param {string} href
 * @returns {string|null}
 */
function getParentHandle(href) {
  if (!href) return null;
  for (const handle of PARENT_HANDLES) {
    if (href.includes(`/products/${handle}`)) return handle;
  }
  return null;
}

/**
 * Reads the URL map from localStorage.
 * @returns {object|null}
 */
function getUrlMap() {
  try {
    const raw = localStorage.getItem('configuratorUrlMap');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Checks whether a valid truck selection is saved.
 * Supports both modern storage (removeItem on reset) and legacy reset (`null` JSON).
 * @returns {boolean}
 */
function hasSavedTruckInfo() {
  try {
    const raw = localStorage.getItem('configuratorTruckInfo');
    if (!raw) return false;

    const truck = JSON.parse(raw);
    if (!truck || typeof truck !== 'object') return false;

    return (
      truck.make &&
      truck.model &&
      truck.year &&
      truck.bed &&
      truck.make !== 'NA' &&
      truck.model !== 'NA' &&
      truck.year !== 'NA' &&
      truck.bed !== 'NA'
    );
  } catch {
    return false;
  }
}

/**
 * Finds the availability container for a rewritten link.
 * Product tiles use `.product`; nav links use the nearest `li`.
 * @param {Element} link
 * @returns {Element|null}
 */
function getAvailabilityContainer(link) {
  return link.closest('.product') || link.closest('li');
}

/**
 * Rewrites all matching links on the page.
 */
function rewriteLinks() {
  const urlMap = getUrlMap();
  const hasSavedTruck = hasSavedTruckInfo();
  const links = document.querySelectorAll(LINK_SELECTOR);

  for (const link of links) {
    // Store original href on first encounter
    if (!link.dataset.originalHref) {
      link.dataset.originalHref = link.getAttribute('href');
    }

    const parentHandle = getParentHandle(link.dataset.originalHref);
    if (!parentHandle) continue;

    const collectionHandle = PRODUCT_TO_COLLECTION[parentHandle];
    const childUrl = urlMap && collectionHandle ? urlMap[collectionHandle] : null;
    const hasChildUrl = Boolean(childUrl);

    if (hasChildUrl) {
      link.setAttribute('href', childUrl);
    } else {
      // Restore original URL
      link.setAttribute('href', link.dataset.originalHref);
    }

    const availabilityContainer = getAvailabilityContainer(link);
    if (availabilityContainer) {
      availabilityContainer.classList.toggle('unavailable', hasSavedTruck && !hasChildUrl);
    }
  }
  rewriteProductBtnText();
}

// Run on initial page load
rewriteLinks();

/**
 * Rewrites CTA text on covers page on all matching links on the page.
 */
function rewriteProductBtnText() {
  const map = {
    'diamondbackhd':          'Shop HD',
    'diamondbackse':          'Shop SE',
    'diamondback-switchback': 'Shop Switchback',
    'diamondback270':         'Shop 270',
    'diamondback180':         'Shop 180',
  };

  document.querySelectorAll('.product-btn').forEach(btn => {
    const href = btn.getAttribute('href') || '';
    const originalHref = btn.getAttribute('data-original-href') || '';

    const matchedKey = Object.keys(map).find(key => originalHref.includes(key));
    if (!matchedKey) return;

    if (originalHref === href) {
      btn.textContent = 'Fit Your Truck';
    } else {
      btn.textContent = map[matchedKey];
    }
  });
}

// Run again after DOM is fully parsed (catches web components that render late)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', rewriteLinks);
} else {
  // DOM already ready — schedule a microtask to catch any pending renders
  queueMicrotask(rewriteLinks);
}

// Observe for dynamically added links (web components, lazy-loaded sections)
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE && /** @type {Element} */ (node).querySelector('a[href*="/products/diamondback"]')) {
        rewriteLinks();
        return;
      }
    }
  }
});
observer.observe(document.body || document.documentElement, { childList: true, subtree: true });

// Same-tab configurator changes
document.addEventListener('configurator:update', rewriteLinks);

// Cross-tab localStorage changes
window.addEventListener('storage', (event) => {
  if (event.key === 'configuratorUrlMap' || event.key === 'configuratorTruckInfo') {
    rewriteLinks();
  }
});

// Shopify section rendering (theme editor, AJAX updates)
document.addEventListener('shopify:section:load', rewriteLinks);

})();
